import { useState, useRef, useEffect, useCallback } from "react";
import HearingTestButton from "../../../components/hearingTestButton/hearingTestButton";
import FormActionButton from "../../../components/formActionButton/formActionButton";
import PillTitle from "../../../components/pillTitle/pillTitle";
import HearingTestTips from "../../../components/hearingTestTips/hearingTestTips";
import StyledSelect from "../../../components/styledSelect/styledSelect";
import HeadphoneTypeSelector from "../../../components/headphoneTypeSelector/headphoneTypeSelector";
import { useI18n } from "../../../utils/translate/i18n/useI18n";
import wordsDb from "../../../assets/words/words_pl.json";
import { AGE_CATEGORY_OPTIONS, GENDER_OPTIONS } from "../../../type/demographics";
import type { VerbalCalibPhase, VerbalParamsNoticeProps } from "../../../type/verbalTest";
import { HEADPHONE_VOLUME_COMPENSATION } from "../../../utils/config";

const CALIBRATION_WORDS = wordsDb.calibration_words;
const ALL_POOL: string[] = wordsDb.wrs_lists.flatMap(l => l.words);

function pickCalibrationWord(): string {
  return CALIBRATION_WORDS[Math.floor(Math.random() * CALIBRATION_WORDS.length)];
}

function buildCalibChoices(target: string): string[] {
  const others = ALL_POOL.filter(w => w.toLowerCase() !== target.toLowerCase());
  const shuffled = [...others].sort(() => Math.random() - 0.5).slice(0, 3);
  return [...shuffled, target].sort(() => Math.random() - 0.5);
}

export function VerbalStartNotice({ onConfirmed }: { onConfirmed: () => void }) {
  const { t } = useI18n();
  return (
    <div className="panel panel--runtest card border-0">
      <div className="card-body">
        <PillTitle as="div" variant="blue-brighter" iconClassName="bi bi-mic" text={t("Badanie słuchu")} />
        <h2 className="mb-3">{t("Audiometria słowna")}</h2>
        <div className="panel--runtest__notice_area" role="note" aria-live="polite">
          <HearingTestButton
            type="button"
            className="btn btn-light btn-lg px-4"
            iconClassName="bi bi-play-circle"
            onClick={onConfirmed}
          >
            {t("Rozpocznij badanie")}
          </HearingTestButton>

          <div>
            <p className="panel--runtest__notice_text mb-4 pt-4 px-0 px-md-4">
              {t("Podczas badania audiometrii słownej będziesz słyszeć w słuchawkach pojedyncze słowa odtwarzane z różną głośnością. Twoim zadaniem jest powtarzanie usłyszanych słów tak dokładnie, jak to możliwe - GŁOŚNO i WYRAŹNIE. Badanie pozwala ocenić, jak dobrze rozumiesz mowę oraz określić poziom głośności, przy którym mowa staje się dla Ciebie zrozumiała.")}
            </p>

            <p className="panel--runtest__notice_text mb-4 px-0 px-md-4">
              {t("Badanie jest dość czasochłonne — należy przeznaczyć na nie około 10–15 minut. W tym czasie powinieneś znajdować się w spokojnym otoczeniu, bez zakłóceń, które mogłyby utrudniać słyszenie i powtarzanie słów.")}
            </p>
          </div>

          <HearingTestTips />
        </div>
      </div>
    </div>
  );
}

export function VerbalParamsNotice({
  onConfirmed,
  onOpenParamsModal,
  onNewTest,
  useMicrophone,
  doWRS,
  doSRT,
  useSentences,
  gender,
  ageCategory,
  headphoneType,
  headphoneConnection,
  onChangeGender,
  onChangeAgeCategory,
  onChangeHeadphoneType,
  onChangeHeadphoneConnection,
}: VerbalParamsNoticeProps) {
  const { t } = useI18n();
  const genderOptions = GENDER_OPTIONS.map(o => ({ value: o.value, label: t(o.label) }));
  const ageCategoryOptions = AGE_CATEGORY_OPTIONS.map(o => ({ value: o.value, label: t(o.label) }));

  const activeParams = [
    { key: "microphone", active: useMicrophone, icon: "bi-mic-fill", label: t("Mikrofon") },
    { key: "wrs", active: doWRS, icon: "bi-list-check", label: t("Test WRS") },
    { key: "srt", active: doSRT, icon: "bi-bar-chart-steps", label: t("Test SRT") },
    { key: "sentences", active: useSentences, icon: "bi-chat-text", label: t("Zdania") },
  ].filter(p => p.active);

  return (
    <div className="panel panel--runtest card border-0">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <PillTitle as="div" variant="blue-brighter" iconClassName="bi bi-sliders2" text={t("Parametry badania")} className="mb-0" />
          <FormActionButton
            type="button"
            variant="outline-white"
            size="xs"
            iconClassName="bi bi-arrow-repeat"
            onClick={onNewTest}
            title={t("Nowy test")}
          >
            {t("Nowy test")}
          </FormActionButton>
        </div>

        <h2 className="mb-3">{t("Parametry badania")}</h2>

        <div className="panel--runtest__notice_area" role="note" aria-live="polite">
          <p className="mb-3 panel--runtest__notice_text pt-2">
            {t("Podanie tych danych pozwoli lepiej dostosować parametry badania, co zwiększy dokładność uzyskanych wyników.")}
          </p>

          <div className="params-notice__demographics">
            <StyledSelect
              label={t("Płeć")}
              icon="bi-person"
              value={gender}
              options={genderOptions}
              onChange={onChangeGender}
              variant="on-dark"
            />
            <StyledSelect
              label={t("Kategoria wiekowa")}
              icon="bi-calendar3"
              value={ageCategory}
              options={ageCategoryOptions}
              onChange={onChangeAgeCategory}
              variant="on-dark"
            />
            <HeadphoneTypeSelector
              headphoneType={headphoneType}
              headphoneConnection={headphoneConnection}
              onChangeHeadphoneType={onChangeHeadphoneType}
              onChangeHeadphoneConnection={onChangeHeadphoneConnection}
            />
          </div>

          <p className="mb-3 panel--runtest__notice_text pt-2">
            {t("W tym kroku możesz zmienić parametry testu lub pozostawić ustawienia domyślne.")}
          </p>
          <p className="mb-3 panel--runtest__notice_text pt-2">
            {t("Zalecamy korzystanie z mikrofonu zamiast przycisków do udzielania odpowiedzi. Dzięki temu wynik testu będzie bardziej wiarygodny. Po usłyszeniu każdego słowa należy powtórzyć je GŁOŚNO i WYRAŹNIE.")}
          </p>

          <div className="d-flex flex-column-reverse flex-md-row justify-content-center gap-2">
            <HearingTestButton
              type="button"
              className="btn btn-outline-light btn-lg px-4"
              iconClassName="bi bi-sliders2"
              onClick={onOpenParamsModal}
            >
              {t("Parametry badania")}
            </HearingTestButton>

            <HearingTestButton
              type="button"
              className="btn btn-light btn-lg px-4"
              iconClassName="bi bi-arrow-right"
              onClick={onConfirmed}
            >
              {t("Dalej")}
            </HearingTestButton>
          </div>

          {activeParams.length > 0 && (
            <div className="params-notice__selected">
              {activeParams.map(p => (
                <span key={p.key} className="params-notice__badge">
                  <i className={`bi ${p.icon}`} aria-hidden="true" />
                  <span>{p.label}</span>
                </span>
              ))}
            </div>
          )}

        </div>
      </div>
    </div>
  );
}

export function VerbalCalibrationNotice({ onConfirmed, onNewTest }: { onConfirmed: () => void; onNewTest: () => void }) {
  const { t } = useI18n();
  return (
    <div className="panel panel--runtest card border-0">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <PillTitle as="div" variant="blue-brighter" iconClassName="bi bi-mic" text={t("Kalibracja")} className="mb-0" />
          <FormActionButton
            type="button"
            variant="outline-white"
            size="xs"
            iconClassName="bi bi-arrow-repeat"
            onClick={onNewTest}
            title={t("Nowy test")}
          >
            {t("Nowy test")}
          </FormActionButton>
        </div>
        <h2 className="mb-3">{t("Przygotuj poziom odsłuchu")}</h2>
        <div className="panel--runtest__notice_area" role="note" aria-live="polite">
          <p className="mb-4 panel--runtest__notice_text pt-3">
            {t("Czy poziom głośności jest ustawiony na 50%? To bardzo ważne dla prawidłowego wyniku testu.")}
          </p>
          <HearingTestButton
            type="button"
            className="btn btn-light btn-lg px-4"
            iconClassName="bi bi-check-circle"
            onClick={onConfirmed}
          >
            {t("Potwierdzam ustawienie głośności")}
          </HearingTestButton>
        </div>
      </div>
    </div>
  );
}

export default function VerbalCalibration({ onDone, onNewTest }: { onDone: () => void; onNewTest: () => void }) {
  const { t } = useI18n();
  const [phase, setPhase] = useState<VerbalCalibPhase>("idle");
  const [currentWord, setCurrentWord] = useState(pickCalibrationWord);
  const [choices, setChoices] = useState<string[]>([]);
  const utteranceRef = useRef<SpeechSynthesisUtterance | null>(null);

  useEffect(() => {
    return () => { window.speechSynthesis.cancel(); };
  }, []);

  const playWord = useCallback((word: string) => {
    window.speechSynthesis.cancel();
    setPhase("playing");

    const utterance = new SpeechSynthesisUtterance(word);
    utterance.lang = "pl-PL";
    utterance.rate = 0.85;
    utterance.volume = 0.7 * HEADPHONE_VOLUME_COMPENSATION;

    const voices = window.speechSynthesis.getVoices();
    const polishVoice = voices.find(v => v.lang.startsWith("pl"));
    if (polishVoice) utterance.voice = polishVoice;

    const showChoices = () => {
      setChoices(buildCalibChoices(word));
      setPhase("choosing");
    };
    utterance.onend = showChoices;
    utterance.onerror = showChoices;

    utteranceRef.current = utterance;
    window.speechSynthesis.speak(utterance);
  }, []);

  const handlePlay = useCallback(() => {
    if (phase === "playing") return;
    const word = pickCalibrationWord();
    setCurrentWord(word);
    playWord(word);
  }, [phase, playWord]);

  const handleChoice = (choice: string) => {
    if (choice.toLowerCase() === currentWord.toLowerCase()) {
      onDone();
    } else {
      const word = pickCalibrationWord();
      setCurrentWord(word);
      playWord(word);
    }
  };

  return (
    <div className="panel panel--runtest card border-0">
      <div className="card-body">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <PillTitle as="div" variant="blue-brighter" iconClassName="bi bi-mic" text={t("Kalibracja mowy")} className="mb-0" />
          <FormActionButton
            type="button"
            variant="outline-white"
            size="xs"
            iconClassName="bi bi-arrow-repeat"
            onClick={onNewTest}
            title={t("Nowy test")}
          >
            {t("Nowy test")}
          </FormActionButton>
        </div>
        <h2 className="mb-3">{t("Przygotuj poziom odsłuchu")}</h2>
        <div className="panel--runtest__notice_area">
          <p className="mb-3">
            {phase === "choosing"
              ? t("Wskaż, jakie słowo zostało wypowiedziane.")
              : <>{t("Załóż słuchawki i odtwórz słowo testowe. Ustaw głośność tak, aby mowę było ledwo słychać.")}<br />{t("Jeśli otoczenie jest głośne, wynik może być zafałszowany.")}</>
            }
          </p>

          {phase === "idle" && (
            <div className="d-grid">
              <HearingTestButton
                type="button"
                className="btn btn-outline-light btn-lg px-4"
                iconClassName="bi bi-play-fill"
                onClick={handlePlay}
              >
                {t("Odtwórz słowo")}
              </HearingTestButton>
            </div>
          )}

          {phase === "playing" && (
            <div className="verbal-test__word-display">
              <span className="verbal-test__speaking-icon"><i className="bi bi-volume-up-fill" /></span>
              <span className="verbal-test__word-status">{t("Słowo odtwarzane…")}</span>
            </div>
          )}

          {phase === "choosing" && (
            <div className="verbal-wrs__choices">
              {choices.map(choice => (
                <button
                  key={choice}
                  type="button"
                  className="verbal-wrs__choice-btn"
                  onClick={() => handleChoice(choice)}
                >
                  {choice}
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
